<template>
  <div class="flex max-h-[82svh] flex-col overflow-hidden">
    <!-- Title -->
    <p class="shrink-0 pb-5 text-xl font-semibold sm:text-2xl">
      {{ t('carIncoming') }}
    </p>

    <div class="min-h-0 flex-1 space-y-3 overflow-y-auto pb-2 pr-1">
      <!-- ══ Step 1: Upload Fahrzeugschein ══════════════════════════════════════ -->
      <div class="rounded-xl border border-obsidian-200 bg-white p-5">
        <!-- Header -->
        <div class="mb-4 flex items-center gap-3">
          <div
            class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-obsidian-900 text-xs font-bold text-white"
          >
            1
          </div>
          <p class="font-semibold text-obsidian-900">{{ t('uploadRegistrationCertificate') }}</p>
        </div>

        <!-- ── Uploaded / existing document preview ── -->
        <template v-if="showDocumentPreview">
          <div class="flex items-start gap-4">
            <button
              type="button"
              class="group relative h-[72px] w-14 shrink-0 overflow-hidden rounded-lg border border-obsidian-200 bg-obsidian-50 transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-obsidian-900 disabled:cursor-default disabled:hover:shadow-none"
              :title="existingDocument ? t('clickToEnlarge') : undefined"
              :disabled="!existingDocument"
              @click="openExistingDocumentPreview"
            >
              <div v-if="showPreviewLoader" class="flex h-full w-full items-center justify-center">
                <span
                  class="h-5 w-5 animate-spin rounded-full border-2 border-obsidian-300 border-t-obsidian-600"
                />
              </div>
              <img
                v-else-if="existingDocument && existingDocumentIsImage && !thumbnailFailed"
                :src="existingDocument.documentFileUrl"
                alt="Fahrzeugschein"
                class="h-full w-full object-contain"
                @error="thumbnailFailed = true"
              />
              <div
                v-else
                class="flex h-full w-full flex-col items-center justify-center gap-1 text-obsidian-300"
              >
                <FileTextIcon class="size-6" />
                <span
                  v-if="existingDocumentIsPdf"
                  class="text-[10px] font-medium text-obsidian-400"
                >
                  PDF
                </span>
              </div>
              <div
                v-if="existingDocument"
                class="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20"
              >
                <ZoomInIcon
                  class="size-4 text-white opacity-0 drop-shadow transition-opacity group-hover:opacity-100"
                />
              </div>
            </button>

            <div class="flex min-w-0 flex-col justify-center gap-2">
              <div v-if="isUploading" class="flex items-center gap-2 text-sm text-obsidian-500">
                <span
                  class="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-obsidian-300 border-t-obsidian-600"
                />
                {{ t('uploading') }}
              </div>
              <div
                v-else-if="isExtracting || isAutoExtracting"
                class="flex items-center gap-2 text-sm text-obsidian-500"
              >
                <span
                  class="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-obsidian-300 border-t-obsidian-600"
                />
                {{ t('extracting') }}
              </div>
              <div
                v-else-if="uploadStatusMessage"
                class="flex items-center gap-2 text-sm"
                :class="uploadStatusMessage.class"
              >
                <component :is="uploadStatusMessage.icon" class="size-4 shrink-0" />
                {{ t(uploadStatusMessage.messageKey) }}
              </div>
              <p v-else class="text-xs text-obsidian-400">{{ t('documentAlreadyUploaded') }}</p>
              <CVButton
                variant="ghost"
                :disabled="isUploadingOrExtracting || isRemovingDocument"
                class="inline-flex w-fit items-center gap-1.5 rounded-md border border-obsidian-200 px-2.5 py-1 text-xs font-medium text-obsidian-600 transition-colors hover:border-obsidian-300 hover:bg-obsidian-50 disabled:opacity-50"
                @click="handleReplaceDocument"
              >
                <RefreshCwIcon class="size-3.5 shrink-0" />
                {{ t('replaceDocument') }}
              </CVButton>
            </div>
          </div>
        </template>

        <!-- ── No document yet / replacing ── -->
        <template v-else>
          <p class="mb-3 text-sm text-obsidian-500">{{ t('uploadHint') }}</p>
          <div class="flex items-center gap-2">
            <FileInputField name="registrationFile" :is-loading="isUploadingOrExtracting" />
            <CVButton
              v-if="replacingDocument"
              variant="ghost"
              class="mt-3"
              :disabled="isUploadingOrExtracting || isRemovingDocument"
              @click="handleCancelReplaceDocument"
            >
              {{ t('keepOldDocument') }}
            </CVButton>
          </div>
        </template>
      </div>

      <!-- ══ Step 2: Enter & verify vehicle data ═══════════════════════════════ -->
      <div class="rounded-xl border border-obsidian-200 bg-white p-5">
        <!-- Header -->
        <div class="mb-4 flex items-start gap-3">
          <div
            class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-obsidian-900 text-xs font-bold text-white"
          >
            2
          </div>
          <div>
            <p class="font-semibold text-obsidian-900">{{ t('fillVehicleData') }}</p>
            <p class="mt-0.5 text-sm text-obsidian-400">{{ t('fillVehicleDataHint') }}</p>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-end lg:grid-cols-3">
          <div>
            <p class="mb-1 text-xs text-obsidian-400">
              {{ t('deliveryDateLastUpdate') }}: {{ formattedAvailableFromDate }}
            </p>
            <DatepickerField name="availableFrom" :label="t('enterNewDeliveryDate')" />
          </div>
          <div>
            <span class="mb-1 flex items-center justify-between">
              <h4>{{ t('carWasRegisteredOn') }}</h4>
              <AutoFillBadge v-if="autoFilledFields.has('lastTuvDate')">
                {{ t('autoFilled') }}
              </AutoFillBadge>
            </span>
            <DatepickerField name="lastTuvDate" />
          </div>
          <div v-if="purchasedUsed">
            <span class="mb-1 flex items-center justify-between">
              <h4>{{ t('firstRegistration') }}</h4>
              <AutoFillBadge v-if="autoFilledFields.has('firstRegistration')">
                {{ t('autoFilled') }}
              </AutoFillBadge>
            </span>
            <DatepickerField name="firstRegistration" />
          </div>
        </div>

        <div class="my-4 border-t border-obsidian-100" />

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <InputField name="insuranceCompany" :label="t('insurance')" />
          <div>
            <span class="mb-1 flex items-center justify-between">
              <h4>{{ t('licencePlate') }}</h4>
              <AutoFillBadge v-if="autoFilledFields.has('licencePlate')">
                {{ t('autoFilled') }}
              </AutoFillBadge>
            </span>
            <InputField name="licencePlate" :placeholder="t('licencePlatePlaceholder')" />
          </div>
          <div>
            <span class="mb-1 flex items-center justify-between">
              <h4>{{ t('fin') }}</h4>
              <AutoFillBadge v-if="autoFilledFields.has('fin')">
                {{ t('autoFilled') }}
              </AutoFillBadge>
            </span>
            <InputField name="fin" />
          </div>
        </div>
      </div>

      <!-- ══ Confirmation & save ════════════════════════════════════════════════ -->
      <div class="rounded-xl border border-obsidian-200 bg-white px-5 py-4">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div class="space-y-3">
            <Checkbox v-model="registrationIsDone">{{ t('registrationIsDone') }}</Checkbox>
            <Checkbox v-model="transferHasBeenOrganized">{{
              t('transferHasBeenOrganized')
            }}</Checkbox>
          </div>
          <CVButton
            variant="default"
            class="w-full sm:w-auto sm:shrink-0"
            :disabled="
              !registrationIsDone ||
              !transferHasBeenOrganized ||
              !showDocumentPreview ||
              isProcessing
            "
            :is-loading="isUpdatingCar"
            @click.prevent="submit"
          >
            {{ t('save') }}
          </CVButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, type Component, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { toTypedSchema } from '@vee-validate/yup';
import { useQueryClient } from '@tanstack/vue-query';
import {
  FileTextIcon,
  ZoomInIcon,
  RefreshCwIcon,
  CircleXIcon,
  CircleCheckIcon,
  TriangleAlertIcon,
} from 'lucide-vue-next';
import type { RegistrationCertificateExtractionDto } from '@carvia/ros-client-types';

import Checkbox from '@/components/Checkbox.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import DatepickerField from '@/components/DatepickerField.vue';
import FileInputField from '@/components/FileInputField.vue';
import InputField from '@/components/InputField.vue';
import AutoFillBadge from '@/components/badges/AutoFillBadge.vue';

import type { Car } from '@/entities/cars/car.entity';
import { CarAcquisitionType } from '@/entities/cars/car-acquisition-type.enum';
import { CarDocumentType } from '@/entities/cars/car-document-type.enum';
import { dateOrString, dateOrStringRequired } from '@/validation/car.schema';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { getDateFormat } from '@/hooks/use-date-format';
import { useUploadAndExtractRegistrationCertificate } from '@/hooks/use-upload-and-extract-registration-certificate';
import { usePdfPreview } from '@/hooks/use-pdf-preview';
import { inferMimeTypeFromFileName } from '@/utils/infer-mime-type-from-file-name';
import { fetchBlobFromUrl } from '@/utils/download-file';
import { useUpdateCar, useExtractRegistrationCertificate } from '@/queries/use-cars';
import {
  CAR_DOCUMENTS_QUERY_KEY,
  useLatestCarDocumentByType,
  useRemoveCarDocument,
} from '@/queries/use-car-documents';

// ─── Status message config ──────────────────────────────────────────────────────

type FileValidationError = 'file_too_large' | 'file_type_not_allowed';

interface StatusMessage {
  icon: Component;
  messageKey: string;
  class: string;
}

const STATUS_MESSAGES: Record<string, StatusMessage> = {
  file_too_large: {
    icon: CircleXIcon,
    messageKey: 'fileTooLarge',
    class: 'text-red-600',
  },
  file_type_not_allowed: {
    icon: CircleXIcon,
    messageKey: 'fileTypeNotAllowed',
    class: 'text-red-600',
  },
  extraction_succeeded: {
    icon: CircleCheckIcon,
    messageKey: 'extractionSuccessShort',
    class: 'font-medium text-green-700',
  },
  extraction_error: {
    icon: TriangleAlertIcon,
    messageKey: 'extractionErrorShort',
    class: 'text-amber-600',
  },
  upload_error: {
    icon: CircleXIcon,
    messageKey: 'uploadError',
    class: 'text-red-600',
  },
};

// ─── Props & emits ──────────────────────────────────────────────────────────────

const props = defineProps<{
  car: Car;
}>();

const emit = defineEmits<{
  (e: 'complete'): void;
}>();

const { t } = useI18n();

const registrationIsDone = ref(false);
const transferHasBeenOrganized = ref(false);
const purchasedUsed = computed(
  () => props.car.acquisitionType === CarAcquisitionType.PURCHASED_USED,
);

const formattedAvailableFromDate = useFormattedDate(
  computed(() => props.car.availableFrom),
  getDateFormat(),
);

// ─── Auto-filled field tracking ─────────────────────────────────────────────────

type AutoFillableField = 'fin' | 'licencePlate' | 'firstRegistration' | 'lastTuvDate';

const autoFilledFields = reactive(new Set<AutoFillableField>());

const autoFilledValues = reactive<Record<AutoFillableField, string | Date | null>>({
  fin: null,
  licencePlate: null,
  firstRegistration: null,
  lastTuvDate: null,
});

// ─── Existing document detection ────────────────────────────────────────────────

const {
  data: existingDocument,
  refetch: refetchExistingDocument,
  isFetching: isFetchingExistingDocument,
} = useLatestCarDocumentByType(props.car.id, CarDocumentType.REGISTRATION_CERTIFICATE);
const replacingDocument = ref(false);
/** ID of the document to delete once the user picks a replacement file. */
const documentIdPendingReplacement = ref<string | null>(null);

const existingDocumentMimeType = computed(
  () =>
    inferMimeTypeFromFileName(existingDocument.value?.documentFileKey) ??
    'application/octet-stream',
);
const existingDocumentIsImage = computed(() => existingDocumentMimeType.value.startsWith('image/'));
const existingDocumentIsPdf = computed(() => existingDocumentMimeType.value === 'application/pdf');
const thumbnailFailed = ref(false);
watch(
  () => existingDocument.value?.documentFileKey,
  () => {
    thumbnailFailed.value = false;
  },
);

const queryClient = useQueryClient();
const { openPdfPreview } = usePdfPreview();

const LATEST_DOC_QUERY_KEY = [
  CAR_DOCUMENTS_QUERY_KEY,
  props.car.id,
  'latest',
  CarDocumentType.REGISTRATION_CERTIFICATE,
] as const;

/**
 * Opens the existing document in the shared preview modal.
 * Always fetch as a blob and let mime inference (filename + blob type) pick
 * image vs PDF. HEIC often lands in S3 as octet-stream; treating that as PDF
 * puts the file in an iframe and the preview fails.
 */
async function openExistingDocumentPreview() {
  if (!existingDocument.value) return;
  const url = existingDocument.value.documentFileUrl;
  openPdfPreview({
    title: t('uploadRegistrationCertificate'),
    fileName: existingDocument.value.documentFileKey,
    mimeType: existingDocumentMimeType.value,
    load: () => fetchBlobFromUrl(url),
    errorMessage: t('couldNotLoadDocument'),
  });
}

// ─── Upload + extraction ────────────────────────────────────────────────────────

const {
  uploadAndExtract,
  isLoading: isUploadingOrExtracting,
  isUploading,
  isExtracting,
  isDone: extractionSucceeded,
  hasExtractionError,
  hasUploadError,
  uploadedDocumentId,
} = useUploadAndExtractRegistrationCertificate();

const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'] as const;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const fileValidationError = ref<FileValidationError | null>(null);

const uploadStatusMessage = computed<StatusMessage | null>(() => {
  if (fileValidationError.value) return STATUS_MESSAGES[fileValidationError.value];
  if (extractionSucceeded.value) return STATUS_MESSAGES.extraction_succeeded;
  if (hasExtractionError.value) return STATUS_MESSAGES.extraction_error;
  if (hasUploadError.value) return STATUS_MESSAGES.upload_error;
  return null;
});

function clearFileSelection() {
  fileValidationError.value = null;
  setFieldValue('registrationFile', undefined);
}

const { mutateAsync: removeCarDocument, isPending: isRemovingDocument } = useRemoveCarDocument();

const { mutateAsync: extractCertificate, isPending: isAutoExtracting } =
  useExtractRegistrationCertificate();

const { mutateAsync: updateCar, isPending: isUpdatingCar } = useUpdateCar();

const isProcessing = computed(
  () =>
    isUploadingOrExtracting.value ||
    isAutoExtracting.value ||
    isRemovingDocument.value ||
    isUpdatingCar.value ||
    isFetchingExistingDocument.value,
);

// ─── Form ───────────────────────────────────────────────────────────────────────

const { handleSubmit, setFieldValue, values } = useForm({
  initialValues: {},
  validationSchema: toTypedSchema(
    yup.object({
      availableFrom: dateOrStringRequired(t('requiredField')),
      lastTuvDate: dateOrStringRequired(t('requiredField')),
      firstRegistration: purchasedUsed.value
        ? dateOrStringRequired(t('requiredField'))
        : dateOrString,
      insuranceCompany: yup.string().required(t('requiredField')),
      licencePlate: yup.string().required(t('requiredField')),
      fin: yup.string().required(t('requiredField')),
      registrationFile: yup.mixed().optional(),
    }),
  ),
});

const showDocumentPreview = computed(
  () =>
    (!replacingDocument.value && !!existingDocument.value) ||
    isUploadingOrExtracting.value ||
    values.registrationFile instanceof File,
);

const showPreviewLoader = computed(() => {
  if (existingDocument.value) {
    return isUploadingOrExtracting.value && replacingDocument.value;
  }
  return isUploadingOrExtracting.value || isFetchingExistingDocument.value;
});

async function handleReplaceDocument() {
  if (isUploadingOrExtracting.value || isRemovingDocument.value) return;

  // Validation error with a selected file but no saved document yet — discard selection.
  if (values.registrationFile instanceof File && !existingDocument.value) {
    clearFileSelection();
    return;
  }

  if (!existingDocument.value) return;

  documentIdPendingReplacement.value = existingDocument.value.id;
  replacingDocument.value = true;
}

function handleCancelReplaceDocument() {
  if (isUploadingOrExtracting.value || isRemovingDocument.value) return;

  replacingDocument.value = false;
  documentIdPendingReplacement.value = null;
  clearFileSelection();
}

function isDateAutoFilled(formValue: unknown, storedValue: string | Date | null): boolean {
  return (
    storedValue instanceof Date &&
    formValue instanceof Date &&
    formValue.getTime() === storedValue.getTime()
  );
}

function clearAutoFilledFields() {
  for (const field of autoFilledFields) {
    const storedValue = autoFilledValues[field];
    const formValue = values[field];

    const isStillAutoFilled =
      storedValue instanceof Date
        ? isDateAutoFilled(formValue, storedValue)
        : formValue === storedValue;

    if (isStillAutoFilled) {
      const clearValue =
        field === 'lastTuvDate' ? undefined : field === 'firstRegistration' ? null : '';
      setFieldValue(field, clearValue);
    }
  }
  autoFilledFields.clear();
  const keys: AutoFillableField[] = ['fin', 'licencePlate', 'firstRegistration', 'lastTuvDate'];
  for (const key of keys) {
    autoFilledValues[key] = null;
  }
}

function applyExtractionResult(extracted: RegistrationCertificateExtractionDto) {
  if (extracted.fin) {
    setFieldValue('fin', extracted.fin);
    autoFilledFields.add('fin');
    autoFilledValues.fin = extracted.fin;
  } else {
    setFieldValue('fin', '');
    autoFilledFields.delete('fin');
    autoFilledValues.fin = null;
  }

  if (extracted.licencePlate) {
    setFieldValue('licencePlate', extracted.licencePlate);
    autoFilledFields.add('licencePlate');
    autoFilledValues.licencePlate = extracted.licencePlate;
  } else {
    setFieldValue('licencePlate', '');
    autoFilledFields.delete('licencePlate');
    autoFilledValues.licencePlate = null;
  }

  if (extracted.firstRegistrationDate) {
    const [year, month, day] = extracted.firstRegistrationDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const targetField = purchasedUsed.value ? 'firstRegistration' : 'lastTuvDate';
    setFieldValue(targetField, date);
    autoFilledFields.add(targetField);
    autoFilledValues[targetField] = date;
  } else {
    const targetField = purchasedUsed.value ? 'firstRegistration' : 'lastTuvDate';
    setFieldValue(targetField, targetField === 'lastTuvDate' ? undefined : null);
    autoFilledFields.delete(targetField);
    autoFilledValues[targetField] = null;
  }
}

// When an existing document is found on open: mark uploaded + re-run extraction to restore fields.
watch(
  existingDocument,
  async (doc) => {
    if (!doc) return;
    if (isUploadingOrExtracting.value) return;
    // Extraction already ran as part of the upload flow in this session.
    if (uploadedDocumentId.value === doc.id) return;
    try {
      const extracted = await extractCertificate(props.car.id);
      applyExtractionResult(extracted);
    } catch {
      // Extraction failure is non-blocking
    }
  },
  { immediate: true },
);

// Detect file selection and kick off upload + AI extraction
watch(
  () => values.registrationFile,
  async (file) => {
    if (!(file instanceof File)) return;

    fileValidationError.value = null;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      fileValidationError.value = 'file_too_large';
      return;
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type as (typeof ALLOWED_MIME_TYPES)[number])) {
      fileValidationError.value = 'file_type_not_allowed';
      return;
    }

    clearAutoFilledFields();

    const oldDocumentId = documentIdPendingReplacement.value;
    if (oldDocumentId) {
      try {
        await removeCarDocument({ carId: props.car.id, documentId: oldDocumentId });
      } catch {
        setFieldValue('registrationFile', undefined);
        return;
      }
      documentIdPendingReplacement.value = null;
      replacingDocument.value = false;
      queryClient.setQueryData(LATEST_DOC_QUERY_KEY, null);
    }

    const extracted = await uploadAndExtract(props.car.id, file, t('registrationCertificateName'));

    await refetchExistingDocument();

    if (!extracted) return;
    applyExtractionResult(extracted);
  },
);

// Clear auto-fill badge once the user manually edits a field
watch(
  () => values.fin,
  (val) => {
    if (val !== autoFilledValues.fin) {
      autoFilledFields.delete('fin');
      autoFilledValues.fin = null;
    }
  },
);
watch(
  () => values.licencePlate,
  (val) => {
    if (val !== autoFilledValues.licencePlate) {
      autoFilledFields.delete('licencePlate');
      autoFilledValues.licencePlate = null;
    }
  },
);
watch(
  () => values.firstRegistration,
  (val) => {
    if (!isDateAutoFilled(val, autoFilledValues.firstRegistration)) {
      autoFilledFields.delete('firstRegistration');
      autoFilledValues.firstRegistration = null;
    }
  },
);
watch(
  () => values.lastTuvDate,
  (val) => {
    if (!isDateAutoFilled(val, autoFilledValues.lastTuvDate)) {
      autoFilledFields.delete('lastTuvDate');
      autoFilledValues.lastTuvDate = null;
    }
  },
);

const submit = handleSubmit(async (formValues) => {
  await updateCar({
    id: props.car.id,
    car: {
      ...formValues,
      ...(purchasedUsed.value ? {} : { firstRegistration: formValues.lastTuvDate }),
      registrationAndOrganizationDoneDate: new Date(),
    },
  });

  emit('complete');
});
</script>

<i18n lang="json">
{
  "en": {
    "licencePlatePlaceholder": "i.e. M-CV 1234",
    "carIncoming": "Car incoming",
    "registrationIsDone": "Registration is done",
    "carWasRegisteredOn": "Car registered on",
    "firstRegistration": "First registration on",
    "insurance": "Insurance",
    "fin": "FIN",
    "licencePlate": "Licence plate",
    "transferHasBeenOrganized": "Delivery to the rental hub has been organized",
    "deliveryDateLastUpdate": "Delivery date as per last update",
    "enterNewDeliveryDate": "Enter new delivery date",
    "uploadRegistrationCertificate": "Upload registration certificate",
    "uploadHint": "FIN, licence plate and first registration date are automatically read after uploading.",
    "fillVehicleData": "Vehicle data",
    "fillVehicleDataHint": "Enter delivery date and registration details. Verify auto-filled fields before saving.",
    "uploading": "Uploading document…",
    "extracting": "Reading FIN, licence plate and first registration date…",
    "extractionSuccessShort": "Data read — please verify below.",
    "extractionErrorShort": "Could not read automatically — please enter manually.",
    "uploadError": "Upload failed. Please try again or enter the values manually.",
    "autoFilled": "Auto-filled",
    "clickToEnlarge": "Click to enlarge",
    "documentAlreadyUploaded": "Already uploaded — document is saved.",
    "replaceDocument": "Replace with a new file",
    "removeDocument": "Remove document",
    "requiredField": "This field is required.",
    "registrationCertificateName": "Registration certificate",
    "fileTooLarge": "File is too large. Maximum allowed size is 10 MB.",
    "fileTypeNotAllowed": "File type not supported. Please upload a JPEG, PNG, WEBP, or PDF.",
    "couldNotLoadDocument": "The document could not be loaded. Please try again.",
    "save": "Save",
    "keepOldDocument": "Keep old file"
  },
  "de": {
    "licencePlatePlaceholder": "zB.: M-CV 1234",
    "carIncoming": "Auto im Anflug",
    "registrationIsDone": "Zulassung ist erledigt",
    "carWasRegisteredOn": "Auto zugelassen am",
    "firstRegistration": "Erstzulassung am",
    "insurance": "Versicherung",
    "fin": "FIN",
    "licencePlate": "Kennzeichen",
    "transferHasBeenOrganized": "Anlieferung zum Rental Hub wurde organisiert",
    "deliveryDateLastUpdate": "Lieferdatum laut letzter Aktualisierung",
    "enterNewDeliveryDate": "Liefertermin neu eingeben",
    "uploadRegistrationCertificate": "Fahrzeugschein hochladen",
    "uploadHint": "Nach dem Hochladen werden FIN, Kennzeichen und Erstzulassungsdatum automatisch ausgelesen.",
    "fillVehicleData": "Fahrzeugdaten",
    "fillVehicleDataHint": "Liefertermin und Zulassungsdaten eingeben. Automatisch befüllte Felder vor dem Speichern prüfen.",
    "uploading": "Dokument wird hochgeladen…",
    "extracting": "FIN, Kennzeichen und Erstzulassungsdatum werden ausgelesen…",
    "extractionSuccessShort": "Ausgelesen – bitte Werte unten prüfen.",
    "extractionErrorShort": "Auslesen fehlgeschlagen – bitte manuell eingeben.",
    "uploadError": "Upload fehlgeschlagen. Bitte erneut versuchen oder Werte manuell eingeben.",
    "autoFilled": "Auto-befüllt",
    "clickToEnlarge": "Klicken zum Vergrößern",
    "documentAlreadyUploaded": "Bereits hochgeladen – Dokument ist gespeichert.",
    "replaceDocument": "Durch neue Datei ersetzen",
    "removeDocument": "Dokument entfernen",
    "requiredField": "Pflichtfeld.",
    "registrationCertificateName": "Fahrzeugschein",
    "fileTooLarge": "Datei zu groß. Maximale Dateigröße: 10 MB.",
    "fileTypeNotAllowed": "Dateiformat nicht unterstützt. Bitte JPEG, PNG, WEBP oder PDF hochladen.",
    "couldNotLoadDocument": "Das Dokument konnte nicht geladen werden. Bitte versuche es erneut.",
    "save": "Speichern",
    "keepOldDocument": "Alte Datei behalten"
  },
  "es": {
    "licencePlatePlaceholder": "p. ej. M-1234-AB",
    "carIncoming": "En llegada",
    "registrationIsDone": "Matriculación hecha",
    "carWasRegisteredOn": "Vehículo matriculado el",
    "firstRegistration": "Primera matriculación el",
    "insurance": "Seguro",
    "fin": "Bastidor (VIN)",
    "licencePlate": "Matrícula",
    "transferHasBeenOrganized": "Entrega al hub de alquiler organizada",
    "deliveryDateLastUpdate": "Fecha de entrega según última actualización",
    "enterNewDeliveryDate": "Introducir nueva fecha de entrega",
    "uploadRegistrationCertificate": "Subir permiso de circulación",
    "uploadHint": "Tras subir el documento, el bastidor, la matrícula y la fecha de primera matriculación se leen automáticamente.",
    "fillVehicleData": "Datos del vehículo",
    "fillVehicleDataHint": "Introduce la fecha de entrega y los datos de matriculación. Revisa los campos autocompletados antes de guardar.",
    "uploading": "Subiendo documento…",
    "extracting": "Leyendo bastidor, matrícula y fecha de primera matriculación…",
    "extractionSuccessShort": "Datos leídos — revísalos abajo.",
    "extractionErrorShort": "No se pudo leer automáticamente — introdúcelos manualmente.",
    "uploadError": "Error al subir. Inténtalo de nuevo o introduce los valores manualmente.",
    "autoFilled": "Autocompletado",
    "clickToEnlarge": "Clic para ampliar",
    "documentAlreadyUploaded": "Ya subido — el documento está guardado.",
    "replaceDocument": "Reemplazar por un archivo nuevo",
    "removeDocument": "Eliminar documento",
    "requiredField": "Campo obligatorio.",
    "registrationCertificateName": "Permiso de circulación",
    "fileTooLarge": "Archivo demasiado grande. Tamaño máximo: 10 MB.",
    "fileTypeNotAllowed": "Formato no compatible. Sube JPEG, PNG, WEBP o PDF.",
    "couldNotLoadDocument": "No se pudo cargar el documento. Inténtalo de nuevo.",
    "save": "Guardar",
    "keepOldDocument": "Conservar archivo anterior"
  }
}
</i18n>
